﻿import { createTheme, ThemeProvider, ScopedCssBaseline, Box, Icon } from "@mui/material";
import React from "react";
import { IGOMAveragesListener } from "../../IGOMAveragesListener";
import { ILiveDataListener } from "../../ILiveDataListener";
import { MainSite } from "../../MainSite";
import { ParameterGroupInfoSet } from "../../ParameterGroupInfoSet";
import { DataProvider } from "../../Contexts/dataContext";
import { TimeRange } from "./GOMCurrentPage";
import { ParameterSchemaStation } from "../../API/Schema/ParameterSchema";
import { ValueBoxComponent } from "../SiteObservations/ValueBoxComponent";
import { ValueGroupHeaderComponent } from "../SiteObservations/ValueGroupHeaderComponent";
import { CurrentVariationFormatter } from "../../Formatters/CurrentVariationFormatter";
import { DefaultGroupFormatter } from "../../Formatters/DefaultGroupFormatter";
import { MaxProfileDepthVariationFormatter } from "../../Formatters/MaxProfileDepthVariationFormatter";
import { SpeedUnits } from "../SiteObservations/Current";
import { TimeRangeButtons } from "../Components/TimeRangeButtons";

interface Props {
    mainSite: MainSite,
    liveDataListeners: ILiveDataListener[],
    GOMAveragesListeners: IGOMAveragesListener[],
    station: ParameterSchemaStation,
    currentSpeedUnits: SpeedUnits,
    timeRangeChanged: (timeRange: TimeRange) => void
}

export function GOMCurrentPageComponent(props: Props): React.JSX.Element {

    // console.log("[GOMCurrentPageComponent] props.mainSite: ", props.mainSite)
    // console.log("[GOMCurrentPageComponent] props.liveDataListeners: ", props.liveDataListeners)
    // console.log("[GOMCurrentPageComponent] props.GOMAveragesListeners: ", props.GOMAveragesListeners)
    // console.log("[GOMCurrentPageComponent] props.mainSite.GOMAveragesManager", props.mainSite.GOMAveragesManager)

    const [timeRange, setTimeRange] = React.useState<TimeRange>("LatestValue");

    const theme = createTheme({ //: https://mui.com/material-ui/customization/typography/#font-size  +  https://mui.com/material-ui/customization/theme-components/#theme-variables
        typography: {
            fontSize: 24,
        },
    });

    // Heights of React components
    const headerHeight = 4;
    const boxHeight = 16.7;

    const getDepthRangeHeader = (type: "Surface" | "Top" | "Middle" | "Bottom") => {

        const convertedUnit = MainSite.getUnitConverter().getConvertedUnits(
            "m", // sourceUnit
            ParameterGroupInfoSet.MeasurementDepth_Total_Current_Peak_deg_Mean.Id
        )

        let minDepth: number;
        let maxDepth: number;

        // If available, take data from horisontal profile.Else, take data from first bin of vertical profile.

        // Define depths in units of m to comply with the unitConverter.
        // Unfortunate since the required depths are given in ft, and therefore requires manual conversion to m... Before being converted back to ft...
        if (type == "Surface") {
            //minDepth = 166; // [ft]
            minDepth = 50.5968; // [m]
            //maxDepth = 479; // [ft]
            maxDepth = 145.999; // [m]
        } else if (type == "Top") {
            //minDepth = 0; // [ft]
            minDepth = 0; // [m]
            //maxDepth = 100; // [ft]
            maxDepth = 30.48; // [m]
        } else if (type == "Middle") {
            // as specified by Luz
            //minDepth = 1000; // [ft]
            minDepth = 304.8; // [m]
            //maxDepth = 1500; // [ft]
            maxDepth = 457.2; // [m]
        } else if (type == "Bottom") {
            // as specified by Luz
            //minDepth = 2200; // [ft]
            minDepth = 670.56; // [m]
            //maxDepth = 2600; // [ft]
            maxDepth = 792.48; // [m]
        }

        const convertedMinDepth = MainSite.getUnitConverter().convert(
            minDepth, // sourceValue
            "m", // sourceUnit
            ParameterGroupInfoSet.MeasurementDepth_Total_Current_Peak_deg_Mean.Id
        )

        const convertedMaxDepth = MainSite.getUnitConverter().convert(
            maxDepth, // sourceValue
            "m", // sourceUnit
            ParameterGroupInfoSet.MeasurementDepth_Total_Current_Peak_deg_Mean.Id
        )

        const header = `${type} (${Math.round(convertedMinDepth)} - ${Math.round(convertedMaxDepth)} ${convertedUnit})`;

        return header
    }

    //// source of CurrentSurface
    // if the GroupValues 'HorizontalCurrentProfile' and 'Total_CurrentSpeed_Surface_mps_Mean' exist,
    // and their sensors of are identical => isCurrentSurfaceFromHorizontalSensor = true
    const sensorHorizontalCurrentProfile = props.mainSite.liveDataManager.latestData?.Stations[props.station.Station.Id].GroupValues.HorizontalCurrentProfile?.[0].Sensor
    const sensorTotal_CurrentSpeed_Surface_mps_Mean = props.mainSite.liveDataManager.latestData?.Stations[props.station.Station.Id].GroupValues.Total_CurrentSpeed_Surface_mps_Mean?.[0].Sensor

    const isCurrentSurfaceFromHorizontalSensor = (
        !!sensorHorizontalCurrentProfile
        && !!sensorTotal_CurrentSpeed_Surface_mps_Mean
        && (sensorHorizontalCurrentProfile == sensorTotal_CurrentSpeed_Surface_mps_Mean)
    ) ? true : false

    return (
        <>
            <React.StrictMode>
                <ThemeProvider theme={theme}>
                    <ScopedCssBaseline>
                        <Box
                            className="container"
                            sx={{
                                bgcolor: 'background.paper',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            {/*Time range buttons*/}
                            <Box
                                sx={{
                                    bgcolor: 'background.paper',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    mb: 2,
                                    width: "100%",
                                }}
                                mb={"-20px"}
                            >
                                <TimeRangeButtons timeRange={timeRange}
                                    setTimeRange={(newTimeRange) => {
                                        setTimeRange(newTimeRange);
                                        props.timeRangeChanged(newTimeRange);
                                    }} />
                            </Box>

                            {/*Value boxes*/}
                            <div
                                style={{
                                    position: 'absolute',
                                    height: '47em',
                                    width: '30em',
                                    top: '60pt'
                                }}
                            >
                                <DataProvider
                                    liveDataListeners={props.liveDataListeners}
                                    GOMAveragesListeners={props.GOMAveragesListeners}
                                    timeRange={timeRange}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <ValueGroupHeaderComponent
                                            text={"Peak Current"}
                                            top={0}
                                            left={0}
                                            height={headerHeight}
                                            width={100}
                                        />
                                        <ValueBoxComponent
                                            station={props.station.Station}
                                            valueFormatter={new DefaultGroupFormatter(ParameterGroupInfoSet.MeasurementDepth_Total_Current_Peak_deg_Mean)}
                                            top={headerHeight}
                                            left={0}
                                            height={boxHeight}
                                            width={100 / 3}
                                            titleText="Depth"
                                        />
                                        <ValueBoxComponent
                                            station={props.station.Station}
                                            valueFormatter={new DefaultGroupFormatter(ParameterGroupInfoSet.Total_CurrentSpeed_Peak_mps_Mean)}
                                            top={headerHeight}
                                            left={100 / 3}
                                            height={boxHeight}
                                            width={100 / 3}
                                            titleText="Speed"
                                        />
                                        <ValueBoxComponent
                                            station={props.station.Station}
                                            valueFormatter={new DefaultGroupFormatter(ParameterGroupInfoSet.Total_CurrentDirection_Peak_deg_Mean)}
                                            top={headerHeight}
                                            left={100 / 3 * 2}
                                            height={boxHeight}
                                            width={100 / 3}
                                            titleText="Direction"
                                        />

                                        <ValueGroupHeaderComponent
                                            text={"Depth Averaged Current Speed"}
                                            top={headerHeight + boxHeight}
                                            left={0}
                                            height={headerHeight}
                                            width={100}
                                        />
                                        <ValueBoxComponent
                                            station={props.station.Station}
                                            valueFormatter={
                                                new CurrentVariationFormatter(
                                                    ParameterGroupInfoSet.Total_CurrentSpeed_Surface_mps_Mean,
                                                    ParameterGroupInfoSet.Total_CurrentDirection_Surface_deg_Mean
                                                )
                                            }
                                            top={headerHeight * 2 + boxHeight}
                                            left={0}
                                            height={boxHeight}
                                            width={100}
                                            titleText={
                                                getDepthRangeHeader(
                                                    isCurrentSurfaceFromHorizontalSensor ? "Surface" : "Top"
                                                )
                                            }
                                        />
                                        <ValueBoxComponent
                                            station={props.station.Station}
                                            valueFormatter={new CurrentVariationFormatter(ParameterGroupInfoSet.Total_CurrentSpeed_Middle_mps_Mean, ParameterGroupInfoSet.Total_CurrentDirection_Middle_deg_Mean)}
                                            top={headerHeight * 2 + boxHeight * 2}
                                            left={0}
                                            height={boxHeight}
                                            width={100}
                                            titleText={getDepthRangeHeader("Middle")}
                                        />
                                        <ValueBoxComponent
                                            station={props.station.Station}
                                            valueFormatter={new CurrentVariationFormatter(ParameterGroupInfoSet.Total_CurrentSpeed_Bottom_mps_Mean, ParameterGroupInfoSet.Total_CurrentDirection_Bottom_deg_Mean)}
                                            top={headerHeight * 2 + boxHeight * 3}
                                            left={0}
                                            height={boxHeight}
                                            width={100}
                                            titleText={getDepthRangeHeader("Bottom")}
                                        />

                                        <ValueGroupHeaderComponent
                                            text={"Profile"}
                                            top={headerHeight * 2 + boxHeight * 4}
                                            left={0}
                                            height={headerHeight}
                                            width={100}
                                        />
                                        <ValueBoxComponent
                                            station={props.station.Station}
                                            valueFormatter={new MaxProfileDepthVariationFormatter(ParameterGroupInfoSet.CurrentProfile)}
                                            top={headerHeight * 3 + boxHeight * 4}
                                            left={0}
                                            height={boxHeight}
                                            width={100}
                                            titleText={"Max Depth"}
                                        />

                                    </Box>
                                </DataProvider>
                            </div>
                        </Box>
                    </ScopedCssBaseline>
                </ThemeProvider>
            </React.StrictMode>
        </>
    );
}